"use client"

import React, { useCallback, useEffect, useMemo, useState, useTransition } from "react"
import { debounce } from "next/dist/server/utils"
import Link from "next/link"
import { useSearchParams } from "next/navigation"
import { loadOrderLineItemsAction, loadOrders } from "@/actions/orderActions"
import { AddressROV1, OrderCtnrROV1 } from "@ub/order-client"
import { ChevronDownIcon, LoaderCircle, Package, PlusIcon, SearchIcon, Undo2Icon } from "lucide-react"
import { useTranslations } from "next-intl"
import { useDebounceValue } from "usehooks-ts"

import { formatDate, formatPrice } from "@/lib/helper"
import { generateProductLink, getLinkWithPrefixFromURLAndLocale } from "@/lib/link-utils/link-utils"
import { resolveStoryblokStoryHref } from "@/lib/link-utils/storyblok-link-resolver"
import { AccountContext } from "@/lib/storefront/account/account-service"
import { OrderSortKey, OrdersResult } from "@/lib/storefront/account/order-service"
import { ExtendedAddressRO } from "@/lib/storefront/addresses/address"
import { OrderLineItemData } from "@/lib/storefront/order/order"
import {
  Button,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/components"
import { DataPagination } from "@/components/ui/components/data-pagination"
import ImageWithBadge from "@/components/ui/components/image-with-badge"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/components/tooltip"
import { cn } from "@/components/ui/helpers/utils"
import { AddressFields } from "@/components/common/addresses/AddressFields"
import AddToBasketButtonClient from "@/components/product/AddToBasketButtonClient"
import AccountProductTrackingLink from "@/components/account/AccountProductTrackingLink"
import { MultilinkStoryblok, UbAccountOrdersViewStoryblok } from "@/components/storyblok/component-types"

interface AccountOrderListClientsProps {
  accountContext: AccountContext
  initialOrders: OrdersResult
  blok?: any
}

function generateOrderLink(order: OrderCtnrROV1) {
  return `/account/order/${order?.data?.id}`
}

function getRevocationLink(link: MultilinkStoryblok | undefined, sbLocale: string): string {
  let href = ""

  if (link?.linktype === "story" && link.cached_url) {
    href = resolveStoryblokStoryHref(link.cached_url, sbLocale, link.story)
  } else if (link?.linktype === "url" && link.url) {
    href = link.url
  }

  return href
}

function AccountOrderLineItemCard(props: {
  orderLineItemData: OrderLineItemData
  order: OrderCtnrROV1
  isoLocale: string
  sbLocale: string
  isB2B: boolean
  blok: UbAccountOrdersViewStoryblok
}) {
  const searchParams = useSearchParams()

  const product = props.orderLineItemData.product
  const orderLineItem = props.orderLineItemData.orderLineItem

  // already contains sbLocale based prefix
  const variantLink = generateProductLink(product?.title, product?.id, searchParams, props.sbLocale)

  const orderLink = getLinkWithPrefixFromURLAndLocale(generateOrderLink(props.order), props.sbLocale)

  return (
    <div className={"w-full"}>
      <div className={"min-h-24 bg-white"}>
        <div className={"flex flex-row gap-6"}>
          {product ? (
            <AccountProductTrackingLink
              prefetch={false}
              className={"cursor-pointer"}
              href={variantLink}
              product={product}
              trackingList="account_orders"
              searchTerm="account_orders"
            >
              <ImageWithBadge
                unoptimized={true}
                className={"aspect-square size-20 object-contain transition-all duration-200 hover:scale-110"}
                src={product?.image ?? ""}
                width={80}
                height={80}
                badgeText={orderLineItem.quantity.value}
                alt={product?.title ?? ""}
              />
            </AccountProductTrackingLink>
          ) : (
            <Link prefetch={false} className={"cursor-pointer"} href={orderLink}>
              <ImageWithBadge
                unoptimized={true}
                className={"aspect-square size-20 object-contain transition-all duration-200 hover:scale-110"}
                src={""}
                width={80}
                height={80}
                badgeText={orderLineItem.quantity.value}
                alt={""}
              />
            </Link>
          )}
          <div className={"flex w-full flex-col items-start justify-start gap-4 sm:flex-row md:flex-col lg:flex-row"}>
            <div className={"grow"}>
              {product ? (
                <AccountProductTrackingLink
                  prefetch={false}
                  className={"cursor-pointer"}
                  href={variantLink}
                  product={product}
                  trackingList="account_orders"
                  searchTerm="account_orders"
                >
                  <div className={"flex w-full flex-col items-start justify-start gap-1"}>
                    <p className={"font-bold"}>{product?.title}</p>

                    <p className={"text-xs lg:text-sm"}>
                      {product?.description}
                      {product?.packagingUnitInfoCompact
                        ? ", " + product?.packagingUnitInfoCompact.split("=")[1].replaceAll(")", "").trim()
                        : ""}
                    </p>
                  </div>
                </AccountProductTrackingLink>
              ) : (
                <Link prefetch={false} className={"cursor-pointer"} href={orderLink}>
                <div className={"flex w-full flex-col items-start justify-start gap-1"}>
                    <>
                      <div className={"flex flex-col gap-y-2 text-xs text-gray-300 lg:text-sm"}>
                        <div>
                          <p className={"font-bold"}>{orderLineItem?.displayName}</p>
                          <p>{orderLineItem?.description}</p>
                        </div>
                        <p className={"text-red-700"}>{props.blok.label_product_not_available}</p>
                      </div>
                    </>
                </div>
                </Link>
              )}
            </div>
            <div>
              {product && (
                <div className={"flex w-full flex-col items-center justify-end gap-2"}>
                  <AddToBasketButtonClient
                    className={"w-full text-xs"}
                    isoLocale={props.isoLocale}
                    isB2BCustomer={props.isB2B}
                    articleIdToAdd={product.id}
                    productData={product}
                    prices={product.effectivePrices}
                    quantity={product?.stepOrderQuantity ?? 1}
                    showSmall={true}
                    pagetyp="account"
                    trackingList="account_orders"
                    searchTerm="account_orders"
                    simulateProductClick={true}
                    text={props.blok.label_product_rebuy}
                  />

                  <AccountProductTrackingLink
                    prefetch={false}
                    href={variantLink}
                    product={product}
                    trackingList="account_orders"
                    searchTerm="account_orders"
                  >
                    <Button size={"smNoGrow"} className="w-full" variant={"secondary"}>
                      <div className={"flex flex-row items-center justify-between gap-2"}>
                        <Package className={"size-4"} />

                        <p className={"text-xs"}>{props.blok.label_order_go_to_product}</p>
                      </div>
                    </Button>
                  </AccountProductTrackingLink>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function AccountOrderCard(props: {
  order: OrderCtnrROV1
  isoLocale: string
  sbLocale: string
  isB2B: boolean
  blok: UbAccountOrdersViewStoryblok
}) {
  const t = useTranslations("storefront")
  const [lineItemsWithProduct, setLineItemsWithProduct] = useState<OrderLineItemData[]>([])
  const [openTooltip, setOpenTooltip] = useDebounceValue<boolean>(false, 100)

  const orderLink = useMemo(() => {
    return getLinkWithPrefixFromURLAndLocale(generateOrderLink(props.order), props.sbLocale)
  }, [props.order, props.sbLocale])

  const revocationLink = useMemo(() => {
    if (props.isB2B) return ""

    return getRevocationLink(props.blok.link_revocation, props.sbLocale)
  }, [props.blok.link_revocation, props.isB2B, props.sbLocale])

  const firstAddress: AddressROV1 | undefined = useMemo(() => {
    if (props?.order?.included?.commonShipToAddress) {
      return Object.values(props.order.included.commonShipToAddress ?? {})[0]
    }
  }, [props.order])

  const addressShortName = useMemo(() => {
    if (firstAddress) {
      const firstName = firstAddress?.firstName ?? ""
      const lastName = firstAddress?.lastName ?? ""
      return (firstName + " " + lastName).trim()
    }
  }, [firstAddress])

  //Lazy load order line items
  useEffect(() => {
    loadOrderLineItemsAction(props.order).then((orderLineItems) => {
      if (orderLineItems) {
        setLineItemsWithProduct(orderLineItems)
      }
    })
  }, [props.order])

  return (
    <div className={"flex w-full flex-col gap-2 rounded-sm bg-white text-sm"}>
      <div
        className={
          "flex w-full flex-row flex-wrap justify-between gap-2 rounded-t-sm border-b border-b-gray-300 bg-gray-200 p-4"
        }
      >
        <div className={"flex flex-col gap-2"}>
          <p className={"font-bold"}>{props.blok?.label_order_date ?? ""}</p>
          <p>{formatDate(props.isoLocale, props.order?.data?.creationDate)}</p>
        </div>
        <div className={"flex flex-col gap-2"}>
          <p className={"font-bold"}>
            {props.blok?.label_order_id ?? ""} {props.order?.data?.documentNumber}
          </p>
          <p>
            <Link
              prefetch={false}
              className={"cursor-pointer text-primary underline hover:text-primary-hover"}
              href={orderLink}
            >
              {props.blok?.order_label_show_details ?? ""}
            </Link>
          </p>
        </div>

        <div className={"flex flex-col gap-2"}>
          <p className={"font-bold"}>{props.blok?.label_order_address ?? ""}</p>
          <TooltipProvider delayDuration={50} skipDelayDuration={100}>
            <Tooltip open={openTooltip} onOpenChange={setOpenTooltip}>
              <TooltipTrigger asChild onTouchEnd={() => !openTooltip && setOpenTooltip(true)}>
                <div className={"flex flex-row items-center gap-0.5"}>
                  <p className={"cursor-pointer"}>{addressShortName}</p>
                  <ChevronDownIcon className={"size-4"} />
                </div>
              </TooltipTrigger>
              <TooltipContent className={"rounded-lg p-4 text-base sm:text-xs"}>
                {props?.order?.included?.commonShipToAddress && (
                  <div>
                    <AddressFields address={firstAddress as ExtendedAddressRO} isB2B={props.isB2B} />
                  </div>
                )}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className={"flex flex-col gap-2"}>
          <p className={"font-bold"}>{props.blok?.label_order_total ?? ""}</p>
          <p>
            {formatPrice(
              props.isoLocale,
              props.order?.data?.totals?.grandTotal?.gross?.value,
              props.order?.data?.totals?.grandTotal?.gross?.currency
            )}
          </p>
        </div>
      </div>

      <div className={"flex flex-col rounded-b-sm bg-white px-4 pb-4"}>
        <div className={"flex flex-col gap-px bg-gray-200"}>
          {lineItemsWithProduct && (
            <>
              {lineItemsWithProduct.map((orderLineItemData) => (
                <div className={"bg-white pt-3.5 pb-5"} key={orderLineItemData.orderLineItem.id}>
                  <AccountOrderLineItemCard
                    isoLocale={props.isoLocale}
                    sbLocale={props.sbLocale}
                    order={props.order}
                    isB2B={props.isB2B}
                    orderLineItemData={orderLineItemData}
                    blok={props.blok}
                  />
                </div>
              ))}
            </>
          )}
        </div>
        {revocationLink && (
          <div className={"flex justify-end pt-4"}>
            <Link prefetch={false} href={revocationLink} target={props.blok.link_revocation?.target}>
              <Button variant={"secondary"} size={"smNoGrow"}>
                <Undo2Icon className={"mr-1 inline-block size-4"} />
                {t("account.common.revoke_contract")}
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}

export default function AccountOrderListClient(props: AccountOrderListClientsProps) {
  const [selectedSort, setSelectedSort] = useState<OrderSortKey>("SORT_BY_DATE_DESC")

  const [selectedFilterYear, setSelectedFilterYear] = useState<string>("-")
  const [selectedSearchText, setSelectedSearchText] = useState<string>("")

  const [orders, setOrders] = useState<OrdersResult>(props.initialOrders)
  const [ordersCache, setOrdersCache] = useState<{ [key: string]: OrdersResult }>({
    "1SORT_BY_DATE_DESC-": props.initialOrders,
  })

  const [searchTerm, setSearchTerm] = useState("")
  const debouncedSearch = debounce(setSelectedSearchText, 800, 10000)
  const [loading, transaction] = useTransition()

  function onSearchTermChange(e: React.ChangeEvent<HTMLInputElement>) {
    setSearchTerm(e.target.value)
    debouncedSearch(e.target.value)
  }

  /*
   * Fill Filter years*/
  const years: string[] = useMemo(() => {
    // 1. Aktuelles Jahr ermitteln
    const currentYear = new Date().getFullYear()
    const endYear = 2018

    // 2. Die Anzahl der Jahre für das Array berechnen
    const yearRange = currentYear - endYear + 1

    // 3. Ein Array mit der passenden Länge erstellen und füllen
    const generatedYears = Array.from({ length: yearRange }, (_, index) => (currentYear - index).toString())

    // 4. Den Platzhalter "-" am Anfang hinzufügen
    return ["-", ...generatedYears]
  }, [])

  function handleSelectSort(currentSort: OrderSortKey) {
    setSelectedSort(currentSort)
  }

  function handleSelectFilter(currentFilter: string) {
    setSelectedFilterYear(currentFilter)
    return true
  }

  const getPagedOrders = useCallback(
    async (page: number) => {
      const cacheKey = `${page}${selectedSort}${selectedFilterYear}${selectedSearchText}`
      console.log("getPagedOrders", cacheKey, ordersCache[cacheKey] != undefined)
      if (ordersCache[cacheKey]) {
        setOrders(ordersCache[cacheKey])
        return
      }
      transaction(async () => {
        const orders = await loadOrders({
          pageSize: 10,
          pageNumber: page - 1,
          sortKey: selectedSort,
          filterYear: selectedFilterYear,
          filterText: selectedSearchText,
        })

        setOrders(orders)
        setOrdersCache((prev) => {
          return { ...prev, cacheKey: orders }
        })
      })
    },
    [ordersCache, selectedFilterYear, selectedSearchText, selectedSort]
  )

  useEffect(() => {
    const fetch = async () => {
      await getPagedOrders(1)
    }

    fetch()
  }, [getPagedOrders])

  return (
    <div className={"flex flex-col gap-4"}>
      <div className="rounded-sm border border-gray-300 bg-gray-200 p-4">
        <div className="grid grid-rows-1 gap-2 lg:grid-cols-[max-content_auto_max-content] lg:grid-rows-2">
          <div className={"flex items-center justify-start lg:order-1"}>
            <p className="text-sm font-light text-gray-800">
              {props.blok.label_order_your_orders ?? "Hier finden Sie Ihre Bestellungen."}
            </p>
          </div>
          <div className={"max-lg:hidden lg:order-2"} />

          <div className={"grid grid-cols-[max-content_auto] grid-rows-1 gap-0.5 lg:order-3 lg:gap-1"}>
            <p className={cn("inline-flex items-center text-sm text-gray-800", { "text-gray-600": loading })}>
              {(props.blok.label_order_year_filter ?? "").replaceAll("%1", orders.filteredSize)}
            </p>
            <Select onValueChange={handleSelectFilter} value={selectedFilterYear} disabled={loading}>
              <SelectTrigger className="my-auto h-8 w-full rounded-lg">
                <SelectValue placeholder="" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {years.map((currentFilter: string, i) => (
                    <SelectItem
                      value={currentFilter}
                      key={currentFilter}
                      onClick={() => handleSelectFilter(currentFilter)}
                    >
                      {i == 0 && years.length > 1 ? `${years[years.length - 1]} - ${years[1]}` : currentFilter}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div className={"flex items-center justify-start lg:order-4"}>
            <Select onValueChange={handleSelectSort} value={selectedSort} disabled={loading}>
              <SelectTrigger className="w-full rounded-lg">
                <SelectValue placeholder="" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {["SORT_BY_DATE_ASC", "SORT_BY_DATE_DESC", "SORT_BY_PRICE_ASC", "SORT_BY_PRICE_DESC"].map(
                    (currentSort: any) => (
                      <SelectItem value={currentSort} key={currentSort} onClick={() => handleSelectSort(currentSort)}>
                        {props.blok[`label_order_${currentSort}`]}
                      </SelectItem>
                    )
                  )}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          <div className={"max-lg:hidden lg:order-5"} />

          <div className={"flex justify-end lg:order-6"}>
            <div className="relative h-fit w-full lg:w-80">
              {!loading && searchTerm != "" ? (
                <button
                  onClick={() => {
                    setSearchTerm("")
                    debouncedSearch("")
                  }}
                  className={"absolute top-1/2 right-2 size-5 -translate-y-1/2 text-gray-400"}
                >
                  <PlusIcon strokeWidth={2} className={"size-5 rotate-45"} />
                </button>
              ) : (
                <div className={"pointer-events-none absolute top-1/2 right-2 size-5 -translate-y-1/2 text-gray-400"}>
                  {loading ? (
                    <LoaderCircle className={"size-5 motion-safe:animate-spin"} />
                  ) : (
                    <SearchIcon strokeWidth={2} className="size-5" />
                  )}
                </div>
              )}
              <input
                type="text"
                placeholder={props.blok.placeholder_search_all_orders}
                value={searchTerm}
                onChange={onSearchTermChange}
                disabled={loading}
                className="
                  w-full rounded-lg border border-gray-300 py-2 pr-10 pl-4 text-sm text-gray-700
                  placeholder:text-gray-400
                  focus:ring-2 focus:ring-blue-500 focus:outline-hidden
                "
              />
            </div>
          </div>
        </div>
      </div>

      <div className={"flex flex-col items-end gap-4"}>
        {orders.orders?.map((order) => (
          <div
            key={order?.data?.documentNumber}
            className={"flex w-full flex-col gap-1 rounded-sm border border-gray-300 p-0"}
          >
            <AccountOrderCard
              order={order}
              isB2B={props.accountContext.isB2B}
              isoLocale={props.accountContext.isoLocale}
              sbLocale={props.accountContext.sbLocale}
              blok={props.blok}
            />
          </div>
        ))}
      </div>
      <DataPagination
        initialPage={1}
        totalPages={Math.ceil(orders.filteredSize / 10)}
        onPageChange={(page) => getPagedOrders(page)}
      ></DataPagination>
    </div>
  )
}
